import React from 'react';

import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonIcon from '@mui/icons-material/Person';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import BallotIcon from '@mui/icons-material/Ballot';
import BlurLinearIcon from '@mui/icons-material/BlurLinear';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import DescriptionIcon from '@mui/icons-material/Description';
import AssistantIcon from '@mui/icons-material/Assistant';
import PregnantWomanIcon from '@mui/icons-material/PregnantWoman';
import GetAppIcon from '@mui/icons-material/GetApp';
import { LocalOffer } from '@mui/icons-material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { GENDER } from 'src/components/questionnaire/Constants';
import LaunchIcon from '@mui/icons-material/Launch';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import NoteIcon from '@mui/icons-material/Note';
import DataObjectIcon from '@mui/icons-material/DataObject';
import InsightsIcon from '@mui/icons-material/Insights';
import LinkIcon from '@mui/icons-material/Link';

// SIDEBAR_VALUE is defined in the dependency-free leaf module ./sidebarValues so
// the Node vocabulary generator can import it without this file's MUI deps.
// Imported here for local use (SIDEBAR_ITEMS below) and re-exported so existing
// importers of `SIDEBAR_VALUE` from './util' are unaffected.
import { SIDEBAR_VALUE } from './sidebarValues';
import {
  config,
  showsReproductiveSection
} from '../pedigree-tool/pedigree/config';
export { SIDEBAR_VALUE };

// LEGEND and LABELS are deliberately absent (TGCLOUD-1674). Both are display
// settings for the whole chart, not information about this family, and reaching
// them through a button called "Family record" is why nobody could find them.
// They now open as modals from the pedigree panel — see label-modal/.
export const SIDEBAR_ITEMS = [
  {
    label: 'TAGS',
    value: SIDEBAR_VALUE.tags,
    icon: <LocalOffer fontSize="medium" />
  },
  {
    label: 'EXTERNAL IDS',
    value: SIDEBAR_VALUE.externalFamilyIds,
    icon: <CollectionsBookmarkIcon fontSize="medium" />
  },
  {
    label: 'FAMILY NOTE',
    value: SIDEBAR_VALUE.familyNote,
    icon: <NoteIcon fontSize="medium" />
  },
  {
    label: 'CLINICAL RECORDS',
    value: SIDEBAR_VALUE.clinicalNotes,
    icon: <DescriptionIcon fontSize="medium" />
  },
  {
    label: 'PEDIGREE ANALYSIS',
    value: SIDEBAR_VALUE.pedigreeMatch,
    icon: <InsightsIcon fontSize="medium" />
  },
  {
    label: 'PATHWAYS',
    value: SIDEBAR_VALUE.pathways,
    icon: <AltRouteIcon fontSize="medium" />
  },
  // CLINICAL INSIGHTS (Profile Match / phenomizer) is intentionally NOT in
  // the family-level sidebar. Its output is specific to one individual's
  // phenotype/disease profile, so it only appears in getUserSidebarItems
  // (the per-individual sidebar) when a family member is selected.
  {
    label: 'FAMILY HISTORY',
    value: SIDEBAR_VALUE.familyHistory,
    icon: <AccountTreeIcon fontSize="medium" />
  },
  {
    label: 'EXPORT',
    value: SIDEBAR_VALUE.export,
    icon: <GetAppIcon fontSize="medium" />
  }
];

// The person view: the accordion list rendered when a node is selected.
//
// Order is deliberate (TGCLOUD-1664), agreed item by item with clinical users
// and pinned by sidebarOrder.test.jsx — don't reshuffle it on a whim. The
// clinical spine the record is actually opened for comes first (PERSON,
// CLINICAL RECORDS, DISEASES, PHENOTYPES, GENOTYPES), then ADD RELATIVE, then
// the per-person clinical and analysis panels, then the incidental ones.
// PATHWAYS sits near the bottom because nobody is currently using it, and
// EXPORT stays last.
//
// LEGEND and LABELS are deliberately absent. Both are pedigree-wide controls,
// not person controls, and they live in the family sidebar (SIDEBAR_ITEMS)
// instead. Nothing is lost by dropping them here: the legend panel renders
// identically in both views, and LabelsMenu's person branch (`isFamilyView`
// false) is a strict subset of its family branch.
// eslint-disable-next-line no-unused-vars
export const getUserSidebarItems = ({
  showPatientAccessLabel,
  showEmrTab,
  // The selected member, used only to decide whether REPRODUCTIVE applies to them
  // (TGCLOUD-1617 item 6). Absent → the item shows, which is the pre-existing behaviour.
  member
}) =>
  [
    {
      label: 'PERSON',
      value: SIDEBAR_VALUE.person,
      icon: <PersonIcon fontSize="medium" />
    },
    // Show when user is proband & don't have email
    // showPatientAccessLabel && {
    //   label: 'SET UP PATIENT ACCESS',
    //   value: SIDEBAR_VALUE.setupPatientAccess,
    //   icon: <VerifiedUserIcon />
    // },
    {
      label: 'CLINICAL RECORDS',
      value: SIDEBAR_VALUE.clinicalNotes,
      icon: <DescriptionIcon fontSize="medium" />
    },
    {
      label: 'DISEASES',
      value: SIDEBAR_VALUE.diagnoses,
      icon: <LocalHospitalIcon fontSize="medium" />
    },
    {
      label: 'PHENOTYPES',
      value: SIDEBAR_VALUE.phenotypes,
      icon: <BallotIcon fontSize="medium" />
    },
    {
      label: 'GENOTYPES',
      value: SIDEBAR_VALUE.genotypes,
      icon: <BlurLinearIcon fontSize="medium" />
    },
    // ADD RELATIVE stays in the panel for now. Whether it belongs up with the
    // clinical spine is deliberately left until the 7.1 right-click menu lands,
    // which may remove the need for it here at all.
    {
      label: 'ADD RELATIVE',
      value: SIDEBAR_VALUE.addRelative,
      icon: <PersonAddIcon fontSize="medium" />
    },
    // Reproductive (pregnancies, Track B) — gated on the repro-nodes flag AND, per TGCLOUD-1617
    // item 6, hidden for an assigned-male individual, who can never carry a pregnancy. The section
    // now lists only the person's OWN pregnancies (the "born of a recorded pregnancy" notice moved
    // to PERSON in item 5), so there is nothing left in it for such a record.
    // `.filter(Boolean)` below drops it when either gate is closed.
    config.ENABLE_REPRO_NODES &&
      showsReproductiveSection(member) && {
        label: 'REPRODUCTIVE',
        value: SIDEBAR_VALUE.reproductive,
        icon: <PregnantWomanIcon fontSize="medium" />
      },
    {
      label: 'RISK SCORES',
      value: SIDEBAR_VALUE.riskFactors,
      icon: <AssignmentLateIcon fontSize="medium" />
    },
    {
      label: 'CLINICAL INSIGHTS',
      value: SIDEBAR_VALUE.suggestions,
      icon: <AssistantIcon fontSize="medium" />
    },
    {
      label: 'PEDIGREE ANALYSIS',
      value: SIDEBAR_VALUE.pedigreeMatch,
      icon: <InsightsIcon fontSize="medium" />
    },
    {
      label: 'FORM SUBMISSIONS',
      value: SIDEBAR_VALUE.formSubmission,
      icon: <AssignmentIcon fontSize="medium" />
    },
    {
      label: 'FAMILY HISTORY',
      value: SIDEBAR_VALUE.familyHistory,
      icon: <AccountTreeIcon fontSize="medium" />
    },
    {
      label: 'TAGS',
      value: SIDEBAR_VALUE.tags,
      icon: <LocalOffer fontSize="medium" />
    },
    // only show EMR if enabled. Not in the order Kathryn agreed — that list
    // covers the panels everyone sees — so it keeps its place next to TAGS,
    // down among the incidental items rather than up with the clinical spine.
    ...(showEmrTab
      ? [
          {
            label: 'EMR',
            value: SIDEBAR_VALUE.emr,
            icon: <LaunchIcon fontSize="medium" />
          }
        ]
      : []),
    {
      label: 'PATHWAYS',
      value: SIDEBAR_VALUE.pathways,
      icon: <AltRouteIcon fontSize="medium" />
    },
    {
      label: 'RECORD STATUS',
      value: SIDEBAR_VALUE.recordStatus,
      icon: (
        <LinkIcon
          sx={{
            fontSize: 'medium'
          }}
        />
      )
    },
    {
      label: 'EXPORT',
      value: SIDEBAR_VALUE.export,
      icon: <GetAppIcon fontSize="medium" />
    }
  ].filter(Boolean);

// Students get the same label and legend modals as everyone else, so this list
// no longer carries them either — it is used for BOTH views, which is how the
// person record still had label config after TGCLOUD-1664 removed it.
// Person-sidebar sections that belong to a security-group module other than
// pedigrees. FORM SUBMISSION lists, sends and opens custom forms through
// /form-builder, and PATHWAYS places the person on pathways through
// /pathways; the server refuses each to a geneticsUser whose groups do not
// grant its module (backend/middleware/securityGroupModuleGate.js) — so the
// section is hidden from them, as that module's own screens already are.
export const SIDEBAR_ITEM_MODULE = Object.freeze({
  [SIDEBAR_VALUE.formSubmission]: 'customForms',
  [SIDEBAR_VALUE.pathways]: 'pathways'
});

// Whether a sidebar item is shown under the caller's security-group modules.
// `allow` is SgAccessContext's allow(moduleKey, action) — true for every role
// but a restricted geneticsUser.
export const allowedBySecurityGroup = (item, allow) => {
  const moduleKey = SIDEBAR_ITEM_MODULE[item?.value];
  return !moduleKey || allow(moduleKey, 'read');
};

export const STUDENT_SIDEBAR_ITEMS = [
  {
    label: 'EXPORT',
    value: SIDEBAR_VALUE.export,
    icon: <GetAppIcon fontSize="medium" />
  }
];

export const checkRelationValid = (selected_gender, deepCopy, formValues) => {
  const spouse = deepCopy.find((p) => p.pid === formValues.spouse);

  const child = deepCopy.some(
    (p) =>
      (p.mother === formValues.pid || p.father === formValues.pid) && !p.hidden
  );

  if (!spouse || !child) return { status: true };

  if (
    (selected_gender === GENDER.female &&
      [GENDER.female, GENDER.unknown].includes(spouse.gender)) ||
    (spouse.gender === GENDER.female &&
      [GENDER.female, GENDER.unknown].includes(selected_gender))
  ) {
    return {
      status: false,
      // English, translated where it is shown (PersonMenu).
      message:
        'This relationship has offspring and no valid sperm donor detected. Please update gender.'
    };
  }

  const relationValid =
    (selected_gender === GENDER.female && spouse.gender === GENDER.male) ||
    (spouse.gender === GENDER.female && selected_gender === GENDER.male);

  if (relationValid) {
    return { status: true };
  } else {
    return {
      status: false,
      message:
        'This relationship has offspring and no valid egg donor detected. Please update gender.'
    };
  }
};

export const replacer = (key, value) => {
  if (typeof value === 'object' && value !== null) {
    if (key === 'parent_node') {
      return undefined;
    }
  }
  return value;
};

const extractFamilyId = (familyMemberId) => {
  if (!familyMemberId) return '';
  const parts = familyMemberId.split('-');
  parts.pop();
  return parts.join('-');
};

export const formatPersonSummary = (data) => {
  if (!data) return;
  const {
    familyMemberId,
    firstName,
    lastName,
    dob,
    hospitalNumber,
    healthcareId,
    proBandId
  } = data;

  const familyId = extractFamilyId(proBandId || familyMemberId);
  const upn = proBandId ? '1' : data.upn;

  const idPart = `${familyId}, UPN ${upn}`;
  const namePart = `${firstName || ''} ${lastName || ''}`.trim();
  const dobPart = dob ? `${dob}` : '';
  const hospitalPart = hospitalNumber
    ? `Hospital Number; ${hospitalNumber}`
    : '';
  const healthcarePart = healthcareId ? `Healthcare ID; ${healthcareId}` : '';

  return { idPart, namePart, dobPart, hospitalPart, healthcarePart };
};

// What the sidebar header says, and why it says two different things.
//
// The identifier alone answered "which record is open?" but not "who am I
// editing?", and this panel scrolls — so once you are deep in a person's
// clinical records the header is the only thing tying the form to a patient.
// Making that a name rather than a member id turns it into a check a clinician
// actually performs.
//
// Person view: NAME over the member id. Family view: a "Family record" overline
// over the G number. The asymmetry is deliberate — it says which sidebar you are
// in without asking you to infer it from the shape of an identifier. The family
// view names nobody on purpose: a surname is not reliably a family's identity
// (several per family is normal), and it is PII, so it would vanish on an
// anonymous deployment where the G number must not.
//
// Pure so the three fallbacks are testable without mounting the drawer.
export const resolveSidebarHeader = ({
  data,
  selectedUser,
  anonymousActive
} = {}) => {
  const memberId = data?.proBandId || data?.familyMemberId;
  if (!memberId) {
    return {
      overline: 'Family record',
      primary: selectedUser?.familyId || '',
      secondary: null
    };
  }
  // Names never render on an anonymous deployment, and a de-identified
  // individual has none to show — fall back to the id as the primary line
  // rather than leaving the header blank.
  const name =
    anonymousActive || data?.isHide
      ? ''
      : [data?.firstName, data?.lastName].filter(Boolean).join(' ').trim();
  if (!name) return { overline: null, primary: memberId, secondary: null };
  return { overline: null, primary: name, secondary: memberId };
};
